// ═══════════════════════════════════════════════════════════════════════════════
// Photo Uploader Component
// ═══════════════════════════════════════════════════════════════════════════════

import { useRef, useCallback, useState, useMemo, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { compressImageForUpload } from '../../lib/imageCompression';
import { validateReceiptFile } from '../../lib/receiptImagePreprocess';
import { LEVEL1_DEFAULTS } from '@fdh/shared';
import { ReceiptImageLightbox } from '../receipt/ReceiptImageLightbox';

type PhotoUploaderProps = {
  photos: File[];
  onPhotosChange: (photos: File[]) => void;
  maxPhotos?: number;
  disabled?: boolean;
};

export const PhotoUploader = ({
  photos,
  onPhotosChange,
  maxPhotos = LEVEL1_DEFAULTS.MAX_TASK_PHOTOS,
  disabled = false,
}: PhotoUploaderProps) => {
  const { t } = useLanguage();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [compressing, setCompressing] = useState(false);

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files || disabled) return;

      const remainingSlots = maxPhotos - photos.length;
      const raw: File[] = [];
      const maxPick = Math.min(files.length, remainingSlots);
      for (let i = 0; i < maxPick; i++) {
        const file = files[i];
        if (validateReceiptFile(file) === null) {
          raw.push(file);
        }
      }

      if (raw.length === 0) return;

      setCompressing(true);
      void (async () => {
        try {
          const compressed = await Promise.all(raw.map((f) => compressImageForUpload(f)));
          onPhotosChange([...photos, ...compressed]);
        } finally {
          setCompressing(false);
        }
      })();
    },
    [photos, onPhotosChange, maxPhotos, disabled]
  );

  const handleRemovePhoto = useCallback(
    (index: number) => {
      if (disabled || compressing) return;
      const newPhotos = photos.filter((_, i) => i !== index);
      onPhotosChange(newPhotos);
    },
    [photos, onPhotosChange, disabled, compressing]
  );

  const handleCameraClick = () => {
    if (!busy && cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const handleGalleryClick = () => {
    if (!busy && galleryInputRef.current) {
      galleryInputRef.current.click();
    }
  };

  const handleKeyDown = (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  const busy = disabled || compressing;
  const showAddSlot = photos.length < maxPhotos && !disabled;

  const previewBlobUrls = useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos]);

  useEffect(() => {
    return () => {
      previewBlobUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [previewBlobUrls]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">
          {t('task.complete.photoSection')}
        </h3>
        <span className="text-xs text-gray-500">
          {photos.length} / {maxPhotos}
        </span>
      </div>

      {compressing ? (
        <p className="text-xs text-primary-600" role="status" aria-live="polite">
          {t('task.complete.compressingPhotos')}
        </p>
      ) : null}

      {/* Photo Preview Grid */}
      <div className="grid grid-cols-3 gap-3">
        {photos.map((photo, index) => (
          <div key={`${photo.name}-${photo.size}-${photo.lastModified}-${index}`} className="relative aspect-square">
            <ReceiptImageLightbox
              previewSrc={previewBlobUrls[index] ?? ''}
              alt={t('task.complete.photoAlt', { n: index + 1 })}
              enlargeAriaLabel={t('task.complete.enlargePhotoAria')}
              closeLabel={t('common.closeViewer')}
              thumbButtonClassName="relative block h-full w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-100 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800"
              thumbImgClassName="h-full w-full object-cover"
            />
            <button
              type="button"
              onClick={() => handleRemovePhoto(index)}
              disabled={busy}
              className="absolute -right-2 -top-2 z-[1] flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white shadow-md transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-50"
              aria-label={t('task.complete.removePhoto')}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}

        {/* Add Photo Placeholder */}
        {showAddSlot && (
          <div
            className={`col-span-3 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-3 sm:col-span-1 sm:aspect-square ${
              compressing ? 'pointer-events-none opacity-60' : ''
            }`}
          >
            <div className="flex h-full flex-col items-center justify-center gap-3">
              <div className="flex w-full items-center justify-center gap-2">
                {/* Camera Button */}
                <button
                  type="button"
                  onClick={handleCameraClick}
                  onKeyDown={handleKeyDown(handleCameraClick)}
                  tabIndex={0}
                  disabled={busy}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600 transition-colors hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-70"
                  aria-label={t('task.complete.takePhoto')}
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </button>

                {/* Gallery Button */}
                <button
                  type="button"
                  onClick={handleGalleryClick}
                  onKeyDown={handleKeyDown(handleGalleryClick)}
                  tabIndex={0}
                  disabled={busy}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-70"
                  aria-label={t('task.complete.choosePhoto')}
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              </div>
              <p className="max-w-[220px] text-center text-xs text-gray-500">
                {t('task.complete.photoHint')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Hidden File Inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          handleFileSelect(e.target.files);
          e.target.value = '';
        }}
        className="hidden"
        disabled={busy}
        aria-hidden="true"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => {
          handleFileSelect(e.target.files);
          e.target.value = '';
        }}
        className="hidden"
        disabled={busy}
        aria-hidden="true"
      />
    </div>
  );
};
